/**
 *
 * @module subscriptionApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/subscription.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/SubscriptionApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import SubscriptionManager from './subscriptionManager';

const Util = Services.Util;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const SubscriptionApiDustUrnReference = Services.Internal.Dust.DustUrnReference.subscription.subscriptionApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class SubscriptionApi
 * @desc Provides ability to access the subscriptions that are available and associated with an account.
 *
 */
export default class SubscriptionApi {

    /**
     *
     * @param {SubscriptionManager} subscriptionManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(subscriptionManager, logger = Logging) {

        const errorMessage = {
            ctor: 'SubscriptionApi(subscriptionManager, logger)',
            get subscriptionManager() {
                return `${this.ctor} needs SubscriptionManager instance`;
            }
        };

        instanceStrict(subscriptionManager, SubscriptionManager, errorMessage.subscriptionManager, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SubscriptionManager}
         *
         */
        this.subscriptionManager = subscriptionManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.subscriptionManager.client.dustEnabled);

        this.logger.info(SubscriptionApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @desc Gets the list of Subscriptions associated with the current account.
     * @returns {Promise<Array<SDK.Subscription.Subscription>>} A promise that completes when the
     * operation has succeeded and returns the requested subscriptions.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     *
     */
    getSubscriptions() {

        const { dustEnabled, logger, subscriptionManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: SubscriptionApi.name,
            urn: SubscriptionApiDustUrnReference.getSubscriptions
        });

        return subscriptionManager.getSubscriptions().catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

}



// WEBPACK FOOTER //
// ./src/subscription/subscriptionApi.js